// The boards, and the search everything navigates by.
//
// `#` wall · `.` pizza slice · `o` fruit pie · ` ` corridor with nothing in it
//
// Both are mirrored left-to-right on purpose: a symmetric maze means neither
// side is the safe side, so where Raf comes from is the only thing that decides
// where you run. Each has one row open at both ends — the tunnel, and the only
// place you can be behind a ghost and in front of him at once.
//
// The fruit pies are deliberately NOT one in each corner, and there are not
// four of them. Three on the standard board and two on the small one, set in
// the open rows rather than the corners, so the board does not lay out its
// power-ups where every maze game since 1980 has put them — and so running for
// one means crossing the middle rather than hugging an edge.
//
// tests/engine.test.js checks BOTH boards for symmetry, for reachable food and
// for dead ends. A pocket with one exit is an unfair death.

const STANDARD = [
  '###################',
  '#........#........#',
  '#.##.###.#.###.##.#',
  '#.##.###.#.###.##.#',
  '#...o.........o...#',
  '#.##.#.#####.#.##.#',
  '#....#.......#....#',
  '###.###.###.###.###',
  '#.................#',
  '#.#####.#.#.#####.#',
  '#........o........#',
  '####.###...###.####',
  '#......#...#......#',
  '#.####.#####.####.#',
  '.......#####.......',
  '#.####.#####.####.#',
  '#......#...#......#',
  '####.###...###.####',
  '#........#........#',
  '#.##.###.#.###.##.#',
  '#..#.....#.....#..#',
  '##.#.#.#####.#.#.##',
  '#....#...#...#....#',
  '#.####.#.#.#.####.#',
  '#.................#',
  '###################',
];

// Fewer tiles, so every tile is drawn bigger — the point of the small board is
// that the characters are large enough to see, not that the game is easier.
const SMALL = [
  '#############',
  '#...........#',
  '#.##.###.##.#',
  '#.....#.....#',
  '#.###.#.###.#',
  '#...........#',
  '###.#####.###',
  '#.....#.....#',
  '..##o.#.o##..',
  '#.....#.....#',
  '###.#####.###',
  '#...........#',
  '#.###.#.###.#',
  '#.....#.....#',
  '#.##.###.##.#',
  '#...........#',
  '#############',
];

export const BOARDS = [
  {
    id: 'standard',
    name: 'Standard',
    blurb: 'The full maze.',
    layout: STANDARD,
    tunnelRow: 14,
    nickStart: { x: 9, y: 24 },
    rafStart: { x: 9, y: 12 },
    tile: 24,
  },
  {
    id: 'small',
    name: 'Small',
    blurb: 'Half the maze, everything twice the size.',
    layout: SMALL,
    tunnelRow: 8,
    nickStart: { x: 6, y: 11 },
    rafStart: { x: 6, y: 5 },
    tile: 36,
  },
];

export const boardById = (id) => BOARDS.find((b) => b.id === id) ?? BOARDS[0];

export const DIRS = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};
export const DIR_LIST = [DIRS.up, DIRS.left, DIRS.down, DIRS.right];
// `-x` on a zero gives -0, and -0 fails a deep-equal against 0. Directions are
// compared and stored all over the engine, so normalise it here once.
export const opposite = (d) => (d ? { x: -d.x || 0, y: -d.y || 0 } : null);
export const sameDir = (a, b) => !!a && !!b && a.x === b.x && a.y === b.y;
export const key = (x, y) => `${x},${y}`;

/**
 * A playable board: the layout plus everything derived from it.
 *
 * These used to be module constants, which is why there was only ever one maze.
 * Everything downstream now takes a board, so adding a third is a data change.
 */
export function makeBoard(id) {
  const spec = boardById(id);
  const { layout } = spec;
  const cols = layout[0].length;
  const rows = layout.length;
  const wrapX = (x) => ((x % cols) + cols) % cols;

  const walkable = (x, y) => (y >= 0 && y < rows && layout[y][wrapX(x)] !== '#');

  /**
   * Breadth-first distance from one tile to every tile it can reach.
   *
   * This is the whole of Raf's intelligence, and the reason he is worth running
   * from: descending this field is a genuine shortest path through the maze,
   * not a greedy step toward you that a wall can trap.
   */
  function distanceField(from) {
    const dist = new Int16Array(cols * rows).fill(-1);
    const at = (x, y) => y * cols + wrapX(x);
    if (!walkable(from.x, from.y)) return dist;
    const start = at(from.x, from.y);
    dist[start] = 0;
    const queue = [start];
    for (let head = 0; head < queue.length; head += 1) {
      const cell = queue[head];
      const cx = cell % cols;
      const cy = (cell - cx) / cols;
      const d = dist[cell];
      for (const dir of DIR_LIST) {
        const nx = wrapX(cx + dir.x);
        const ny = cy + dir.y;
        if (!walkable(nx, ny)) continue;
        const n = at(nx, ny);
        if (dist[n] !== -1) continue;
        dist[n] = d + 1;
        queue.push(n);
      }
    }
    return dist;
  }

  const distAt = (field, x, y) => field[(y * cols) + wrapX(x)];

  /** Every corridor cell, nearest-to-furthest from Nick — used to place extras. */
  function corridors() {
    const out = [];
    for (let y = 0; y < rows; y += 1) {
      for (let x = 0; x < cols; x += 1) if (layout[y][x] !== '#') out.push({ x, y });
    }
    return out;
  }

  // The four scatter corners that used to live here are gone with the wave that
  // used them: Rafs now guard pizza instead of retreating to a fixed corner, so
  // there is nowhere on the board that is reliably empty.

  return {
    ...spec, cols, rows, wrapX, walkable, distanceField, distAt, corridors,
  };
}
